'use client';

import { c as _c } from "react/compiler-runtime";
import { useState } from 'react';
import { FEATURE_FLAGS } from '@bridge/constants';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { Body } from '../typography/Body';
import { Heading } from '../typography/Heading';
import { Button } from '../components/Button';
import { FlexContainer } from '../components/FlexContainer';
import { Input } from '../components/Input';
import { FlagOverrideRow } from '../components/FlagOverrideRow';
import { useDevLdFlagOverridesStore } from './dev-overrides-store';
const STATIC_FLAG_KEYS: readonly string[] = Object.values(FEATURE_FLAGS);

/**
 * Dev-only list of LaunchDarkly flags with per-flag override toggles. Rows are
 * the union of `FEATURE_FLAGS` values (always shown so engineers can override
 * locally even without LD connected) and the live `useFlags()` payload (covers
 * any flag the LD server returns that isn't yet in `FEATURE_FLAGS`). Overrides
 * persist to localStorage via `useDevLdFlagOverridesStore`.
 */
export function LaunchDarklyFlagsList() {
  const $ = _c(47);
  const flags = useFlags();
  const overrides = useDevLdFlagOverridesStore(_temp);
  const setOverride = useDevLdFlagOverridesStore(_temp2);
  const clearOverrides = useDevLdFlagOverridesStore(_temp3);
  const [filter, setFilter] = useState("");
  let T0;
  let T1;
  let allKeys;
  let filteredKeys;
  let t0;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  if ($[0] !== clearOverrides || $[1] !== filter || $[2] !== flags || $[3] !== overrides || $[4] !== setOverride) {
    allKeys = Array.from(new Set([...STATIC_FLAG_KEYS, ...Object.keys(flags)])).sort(_temp4);
    const needle = filter.trim().toLowerCase();
    filteredKeys = needle ? allKeys.filter(key => key.toLowerCase().includes(needle)) : allKeys;
    let t6;
    if ($[15] !== overrides) {
      t6 = Object.keys(overrides);
      $[15] = overrides;
      $[16] = t6;
    } else {
      t6 = $[16];
    }
    const activeCount = t6.length;
    T1 = FlexContainer;
    t3 = "gap-4 w-full";
    t4 = "column";
    let t7;
    if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = event => setFilter(event.target.value);
      $[17] = t7;
    } else {
      t7 = $[17];
    }
    let t8;
    if ($[18] !== filter) {
      t8 = <Input aria-label="Filter LaunchDarkly flags" onChange={t7} placeholder={"Filter by name\u2026"} value={filter} />;
      $[18] = filter;
      $[19] = t8;
    } else {
      t8 = $[19];
    }
    const t9 = activeCount === 0;
    const t10 = activeCount > 0 ? ` (${activeCount})` : "";
    let t11;
    if ($[20] !== clearOverrides || $[21] !== t10 || $[22] !== t9) {
      t11 = <Button disabled={t9} onClick={clearOverrides} variant="tertiary">Reset all{t10}</Button>;
      $[20] = clearOverrides;
      $[21] = t10;
      $[22] = t9;
      $[23] = t11;
    } else {
      t11 = $[23];
    }
    if ($[24] !== t11 || $[25] !== t8) {
      t5 = <FlexContainer className="gap-2 w-full" items="center" justify="between">{t8}{t11}</FlexContainer>;
      $[24] = t11;
      $[25] = t8;
      $[26] = t5;
    } else {
      t5 = $[26];
    }
    T0 = FlexContainer;
    t0 = "gap-2 w-full";
    t1 = "column";
    let t12;
    if ($[27] !== flags || $[28] !== overrides || $[29] !== setOverride) {
      t12 = key_0 => {
        const serverLoaded = Object.prototype.hasOwnProperty.call(flags, key_0);
        const serverRaw = serverLoaded ? flags[key_0] : undefined;
        const isInStaticList = STATIC_FLAG_KEYS.includes(key_0);
        const assumedServerValue = serverLoaded ? serverRaw : false;
        const isBooleanCapable = !serverLoaded || typeof serverRaw === "boolean";
        const override = overrides[key_0];
        const isOverridden = override !== undefined;
        const effectiveValue = isOverridden ? override : Boolean(assumedServerValue);
        return <FlagOverrideRow checked={isBooleanCapable ? effectiveValue : undefined} description={<>Server:{" "}{serverLoaded ? typeof serverRaw === "boolean" ? String(serverRaw) : JSON.stringify(serverRaw) : "(not loaded)"}{isOverridden ? " \xB7 overridden" : ""}</>} fallbackControl={<Body className="text-content-secondary" size="sm">(non-boolean)</Body>} key={key_0} name={key_0} nameSuffix={!isInStaticList && <Body className="text-content-secondary shrink-0" size="sm">(not in FEATURE_FLAGS)</Body>} onCheckedChange={next => setOverride(key_0, next === assumedServerValue ? null : next)} onReset={() => setOverride(key_0, null)} showReset={isOverridden} />;
      };
      $[27] = flags;
      $[28] = overrides;
      $[29] = setOverride;
      $[30] = t12;
    } else {
      t12 = $[30];
    }
    t2 = filteredKeys.map(t12);
    $[0] = clearOverrides;
    $[1] = filter;
    $[2] = flags;
    $[3] = overrides;
    $[4] = setOverride;
    $[5] = T0;
    $[6] = T1;
    $[7] = allKeys;
    $[8] = filteredKeys;
    $[9] = t0;
    $[10] = t1;
    $[11] = t2;
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
  } else {
    T0 = $[5];
    T1 = $[6];
    allKeys = $[7];
    filteredKeys = $[8];
    t0 = $[9];
    t1 = $[10];
    t2 = $[11];
    t3 = $[12];
    t4 = $[13];
    t5 = $[14];
  }
  let t6;
  if ($[31] !== allKeys || $[32] !== filter || $[33] !== filteredKeys.length) {
    t6 = filteredKeys.length === 0 && <FlexContainer className="py-8" items="center" justify="center"><Heading size="xs">{allKeys.length === 0 ? "No LaunchDarkly flags loaded" : `No flags match “${filter}”`}</Heading></FlexContainer>;
    $[31] = allKeys;
    $[32] = filter;
    $[33] = filteredKeys.length;
    $[34] = t6;
  } else {
    t6 = $[34];
  }
  let t7;
  if ($[35] !== T0 || $[36] !== t0 || $[37] !== t1 || $[38] !== t2 || $[39] !== t6) {
    t7 = <T0 className={t0} direction={t1}>{t2}{t6}</T0>;
    $[35] = T0;
    $[36] = t0;
    $[37] = t1;
    $[38] = t2;
    $[39] = t6;
    $[40] = t7;
  } else {
    t7 = $[40];
  }
  let t8;
  if ($[41] !== T1 || $[42] !== t3 || $[43] !== t4 || $[44] !== t5 || $[45] !== t7) {
    t8 = <T1 className={t3} direction={t4}>{t5}{t7}</T1>;
    $[41] = T1;
    $[42] = t3;
    $[43] = t4;
    $[44] = t5;
    $[45] = t7;
    $[46] = t8;
  } else {
    t8 = $[46];
  }
  return t8;
}
function _temp4(a, b) {
  return a.localeCompare(b);
}
function _temp3(state_1) {
  return state_1.clearOverrides;
}
function _temp2(state_0) {
  return state_0.setOverride;
}
function _temp(state) {
  return state.overrides;
}